import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Config } from '../../common/config';
import AlertModal from '../../components/alertModal';
import LeftMenu from '../../components/leftMenu';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import useStoreNo from '../../hooks/useStoreNo';
import { isSuccessResponse, getErrorMessage } from '../../components/utils';

const getTodayStr = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

/**
 * 상점주 프로모션 생성/수정 (VOUCHER / COUPON / GAME). issuerType=STORE 는 서버가 강제.
 * 백엔드: naegift-api-promotion admin/promotion
 *  - 생성: POST /admin/promotion (create-scoped-promotion.dto)
 *  - 수정: PATCH /admin/promotion/:promotionNo (update-promotion.dto) — name/totalAmount/totalCount/
 *    couponDiscountAmount만 가능. perUserLimit·couponCode·종류는 생성 시에만 정해지고 수정 불가.
 *    상태가 ENDED면 서버가 수정을 거부한다.
 * GAME은 회차 구성이 이 범용 폼과 다르고 회차/경품 관리 화면(gameDetail.js)이 따로 있어서,
 * 수정 모드로 들어오면 그 화면으로 리다이렉트한다.
 * ?promotionNo=123 쿼리로 들어오면 수정 모드.
 */
export default function PromotionContent() {
  const navigate = useNavigate();
  const storeNo = useStoreNo();
  const { fetchData } = useAxiosFetch();
  const [searchParams] = useSearchParams();
  const editingNo = searchParams.get('promotionNo');
  const isEdit = !!editingNo;

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState('');
  const [locked, setLocked] = useState(false); // ENDED 등 수정 불가 상태

  const [promotionType, setPromotionType] = useState('VOUCHER');
  const [name, setName] = useState('');
  const [startDttm, setStartDttm] = useState('');
  const [endDttm, setEndDttm] = useState('');
  const [couponDiscountAmount, setCouponDiscountAmount] = useState('');
  const [totalCount, setTotalCount] = useState('');
  const [perUserLimit, setPerUserLimit] = useState('1');
  const [couponCode, setCouponCode] = useState('');
  const [totalAmountPerStore, setTotalAmountPerStore] = useState('');
  const [roundCnt, setRoundCnt] = useState('1');
  const [missSlotCnt, setMissSlotCnt] = useState('5');
  const [busy, setBusy] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState('');

  const showMsg = msg => {
    setModalMsg(msg);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const ret = await fetchData({
          url: `${Config.API_SERVER}promotion/${editingNo}`,
          method: 'get',
        });
        const p = ret?.data?.data?.promotion;
        if (!isSuccessResponse(ret) || !p) {
          setLoadError('프로모션 정보를 불러오지 못했습니다.');
          return;
        }
        setPromotionType(p.promotionType);
        setName(p.name || '');
        setStartDttm(p.startDttm ? String(p.startDttm).slice(0, 10) : '');
        setEndDttm(p.endDttm ? String(p.endDttm).slice(0, 10) : '');
        setCouponDiscountAmount(p.couponDiscountAmount != null ? String(p.couponDiscountAmount) : '');
        setTotalCount(p.totalCount != null ? String(p.totalCount) : '');
        setPerUserLimit(p.perUserLimit != null ? String(p.perUserLimit) : '1');
        setCouponCode(p.couponCode || '');
        setTotalAmountPerStore(p.totalAmount != null ? String(p.totalAmount) : '');
        // GAME은 회차 구성이 이 범용 폼과 달라서(회차 수/꽝 슬롯 등) 전용 관리 화면으로 보낸다.
        if (p.promotionType === 'GAME') {
          navigate(`/${storeNo}/promotion-game?promotionNo=${editingNo}`, { replace: true });
          return;
        } else if (p.status === 'ENDED') {
          setLocked(true);
          setLoadError('종료된 프로모션은 수정할 수 없습니다.');
        }
      } catch (e) {
        setLoadError('프로모션 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editingNo]);

  const submit = async () => {
    if (!name.trim()) return showMsg('프로모션명을 입력하세요.');
    if (startDttm && endDttm && startDttm > endDttm) {
      return showMsg('종료일이 시작일보다 빠를 수 없습니다.');
    }
    if (!isEdit && startDttm && startDttm < getTodayStr()) {
      return showMsg('시작일은 오늘 이후로 설정해 주세요.');
    }

    if (isEdit) {
      const body = { name: name.trim(), startDttm: startDttm || '', endDttm: endDttm || '' };
      if (promotionType === 'COUPON') {
        const amt = Number(couponDiscountAmount);
        if (!amt || amt < 1) return showMsg('쿠폰 할인액을 입력하세요.');
        body.couponDiscountAmount = amt;
        body.totalCount = totalCount ? Number(totalCount) : null;
      } else {
        const budget = Number(totalAmountPerStore);
        if (!budget || budget < 1) return showMsg('상점 예산을 입력하세요.');
        body.totalAmount = budget;
      }
      setBusy(true);
      try {
        const ret = await fetchData({
          url: `${Config.API_SERVER}promotion/${editingNo}`,
          method: 'patch',
          data: body,
        });
        if (isSuccessResponse(ret)) {
          navigate(`/${storeNo}/promotion-list`);
        } else {
          showMsg(getErrorMessage(ret) || '수정에 실패했습니다.');
        }
      } catch (e) {
        showMsg('수정에 실패했습니다.');
      } finally {
        setBusy(false);
      }
      return;
    }

    const body = { promotionType, name: name.trim() };
    if (startDttm) body.startDttm = startDttm;
    if (endDttm) body.endDttm = endDttm;
    if (promotionType === 'COUPON') {
      const amt = Number(couponDiscountAmount);
      if (!amt || amt < 1) return showMsg('쿠폰 할인액을 입력하세요.');
      const cnt = Number(totalCount);
      if (!cnt || cnt < 1) return showMsg('총 발행 매수를 입력하세요.');
      body.couponDiscountAmount = amt;
      body.totalCount = cnt;
      if (perUserLimit) body.perUserLimit = Number(perUserLimit);
      if (couponCode.trim()) body.couponCode = couponCode.trim();
    } else if (promotionType === 'GAME') {
      const rc = Number(roundCnt);
      const mc = Number(missSlotCnt);
      if (!rc || rc < 1) return showMsg('회차 수를 입력하세요.');
      if (missSlotCnt === '' || mc < 0) return showMsg('회차당 꽝 칸 수를 입력하세요.');
      body.roundCnt = rc;
      body.missSlotCnt = mc;
    } else {
      const budget = Number(totalAmountPerStore);
      if (!budget || budget < 1) return showMsg('상점 예산을 입력하세요.');
      body.totalAmountPerStore = budget;
    }

    setBusy(true);
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion`,
        method: 'post',
        data: body,
      });
      if (isSuccessResponse(ret)) {
        const createdNo = ret?.data?.data?.promotionNo;
        if (promotionType === 'GAME' && createdNo) {
          navigate(`/${storeNo}/promotion-game?promotionNo=${createdNo}`);
        } else {
          navigate(`/${storeNo}/promotion-list`);
        }
      } else {
        showMsg(getErrorMessage(ret) || '생성에 실패했습니다.');
      }
    } catch (e) {
      showMsg('생성에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AlertModal open={modalOpen} close={() => setModalOpen(false)} isMessage>
        {modalMsg}
      </AlertModal>

      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>홈</NavLink>
          </li>
          <li>
            <NavLink to={`/${storeNo}/promotion-list`}>프로모션 관리</NavLink>
          </li>
          <li>{isEdit ? '프로모션 수정' : '새 프로모션'}</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="promotion" />

          <section className="rightcontant_box">
            <h2 className="tit_01">{isEdit ? '프로모션 수정' : '새 프로모션'}</h2>

            {loading ? (
              <p style={{ padding: '20px 0' }}>불러오는 중...</p>
            ) : (
              <article className="form_card contants_box_03">
                {loadError && (
                  <p style={{ color: '#c0392b', fontSize: 13, marginBottom: 12 }}>{loadError}</p>
                )}
                <table className="member_box">
                  <tbody>
                    <tr>
                      <th>종류</th>
                      <td>
                        {isEdit ? (
                          <span>{promotionType === 'COUPON' ? '쿠폰' : '바우처'} <span style={{ color: '#888', fontSize: 12 }}>(생성 후 변경 불가)</span></span>
                        ) : (
                          <select
                            className="input_01 select_01"
                            value={promotionType}
                            onChange={e => setPromotionType(e.target.value)}
                          >
                            <option value="VOUCHER">바우처 — 상품 가격에 미리 반영되는 할인</option>
                            <option value="COUPON">쿠폰 — 손님이 받아서 결제·매장에서 쓰는 할인권</option>
                            <option value="GAME">룰렛 — 앱 공지사항 이벤트(회원 1인 1회)</option>
                          </select>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>프로모션명</th>
                      <td>
                        <input
                          className="input_01"
                          type="text"
                          maxLength={100}
                          value={name}
                          disabled={locked}
                          onChange={e => setName(e.target.value)}
                          placeholder="예) 봄맞이 1,000원 할인"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>유효기간</th>
                      <td>
                        <input
                          className="input_01_01"
                          type="date"
                          value={startDttm}
                          disabled={locked}
                          min={isEdit ? undefined : getTodayStr()}
                          onChange={e => setStartDttm(e.target.value)}
                        />
                        {' ~ '}
                        <input
                          className="input_01_01"
                          type="date"
                          value={endDttm}
                          disabled={locked}
                          min={isEdit ? undefined : (startDttm || getTodayStr())}
                          onChange={e => setEndDttm(e.target.value)}
                        />
                        <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                          비워두면 무기한(직접 "중지"할 때까지 계속 진행)
                        </div>
                      </td>
                    </tr>

                    {promotionType === 'COUPON' ? (
                      <>
                        {/* [2026-10-22 A-F4] 쿠폰은 손님이 앱 쿠폰받기에서 직접 받는다 */}
                        <tr>
                          <th>노출</th>
                          <td style={{ color: '#666', fontSize: 13 }}>
                            등록하면 시작일부터 내기프트 앱 <b>쿠폰받기</b>에 노출되고, 손님이 직접 받아 쿠폰함에 담습니다.
                          </td>
                        </tr>
                        <tr>
                          <th>쿠폰 할인액</th>
                          <td>
                            <input
                              className="input_01_01"
                              style={{ textAlign: 'right' }}
                              type="text"
                              value={couponDiscountAmount}
                              disabled={locked}
                              onChange={e =>
                                setCouponDiscountAmount(e.target.value.replace(/[^0-9]/g, ''))
                              }
                            />
                            &ensp;원
                          </td>
                        </tr>
                        <tr>
                          <th>총 발행 매수</th>
                          <td>
                            <input
                              className="input_01_01"
                              style={{ textAlign: 'right' }}
                              type="text"
                              value={totalCount}
                              disabled={locked}
                              onChange={e => setTotalCount(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                            &ensp;장
                          </td>
                        </tr>
                        <tr>
                          <th>1인 받기 한도</th>
                          <td>
                            <input
                              className="input_01_01"
                              style={{ textAlign: 'right' }}
                              type="text"
                              value={perUserLimit}
                              disabled={isEdit}
                              onChange={e => setPerUserLimit(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                            &ensp;장
                            {isEdit && (
                              <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>
                                생성 후 변경 불가
                              </span>
                            )}
                          </td>
                        </tr>
                        {/* [2026-10-20] 쿠폰 코드 입력칸 숨김 — 넣으면 "코드 입력형"이 되는데 손님이 코드를 입력할 화면·결제 API 경로가 없어 발급도 사용도 안 되는 쿠폰이 된다(ledger §15). 코드형을 실제로 쓰게 되면 다시 살린다. 기본값이 빈 값이라 숨겨도 요청에 couponCode 는 안 실린다.
                        <tr>
                          <th>코드 (선택)</th>
                          <td>
                            <input
                              className="input_01"
                              type="text"
                              maxLength={30}
                              value={couponCode}
                              disabled={isEdit}
                              onChange={e => setCouponCode(e.target.value)}
                              placeholder="입력하면 코드 입력형 쿠폰"
                            />
                            {isEdit && (
                              <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>
                                생성 후 변경 불가
                              </span>
                            )}
                          </td>
                        </tr>
                        */}
                      </>
                    ) : promotionType === 'GAME' ? (
                      <>
                        <tr>
                          <th>회차 수</th>
                          <td>
                            <input
                              className="input_01_01"
                              style={{ textAlign: 'right' }}
                              type="text"
                              value={roundCnt}
                              onChange={e => setRoundCnt(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                            &ensp;회차
                          </td>
                        </tr>
                        <tr>
                          <th>회차당 꽝 칸 수</th>
                          <td>
                            <input
                              className="input_01_01"
                              style={{ textAlign: 'right' }}
                              type="text"
                              value={missSlotCnt}
                              onChange={e => setMissSlotCnt(e.target.value.replace(/[^0-9]/g, ''))}
                            />
                            &ensp;개&emsp;
                            <span style={{ color: '#888', fontSize: 12 }}>
                              상품 등록·시작·뽑기 등 회차 관리는 생성 후 상세 화면에서 진행합니다
                            </span>
                          </td>
                        </tr>
                        {/* [2026-10-22 B-F3] 앱 공지사항 이벤트로 진행 — 공지는 발행처가 공지 관리에서 등록 */}
                        <tr>
                          <th>참여 방법</th>
                          <td style={{ color: '#666', fontSize: 13 }}>
                            내기프트 앱 공지사항의 이벤트에서 로그인한 회원이 1인 1회 참여합니다. 앱 공지사항 노출은 공지 관리에서 룰렛이벤트 공지를 등록해 주세요.
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <th>상점 예산</th>
                        <td>
                          <input
                            className="input_01_01"
                            style={{ textAlign: 'right' }}
                            type="text"
                            value={totalAmountPerStore}
                            disabled={locked}
                            onChange={e =>
                              setTotalAmountPerStore(e.target.value.replace(/[^0-9]/g, ''))
                            }
                          />
                          &ensp;원&emsp;
                          <span style={{ color: '#888', fontSize: 12 }}>
                            {isEdit
                              ? '이미 사용된 금액은 그대로 유지되고, 예산 총액만 바뀝니다'
                              : '이 예산에서 기프트 할인분이 차감됩니다'}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="button_box_01" style={{ marginTop: 20 }}>
                  <button
                    className="button_04 button_gray_line"
                    onClick={() => navigate(`/${storeNo}/promotion-list`)}
                  >
                    취소
                  </button>
                  <button
                    className="button_05 button_black"
                    disabled={busy || locked}
                    onClick={submit}
                  >
                    {busy ? '저장 중...' : isEdit ? '수정 저장' : '프로모션 생성'}
                  </button>
                </div>
              </article>
            )}
          </section>
        </section>
      </main>
    </>
  );
}
